import React, { useState } from 'react';
import { Lock, LogIn, AlertCircle } from 'lucide-react';
import { dashboardApi } from '../services/api';

/**
 * Sign-in screen. Shown in place of the whole app until there is a session.
 *
 * The session lives in an httpOnly cookie set by the server, so nothing
 * sensitive is kept in React state or localStorage where a script could read it.
 */
const Login = ({ onSignedIn }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { data } = await dashboardApi.login({ username, password });
      onSignedIn(data.user);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not sign in');
      setPassword('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
      <div className="w-full max-w-sm">
        <div className="bg-white rounded-lg shadow p-8">
          <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-blue-50">
            <Lock className="w-6 h-6 text-blue-600" />
          </div>
          <h1 className="text-xl font-bold text-center text-gray-900">Affiwise Outreach</h1>
          <p className="mt-1 mb-6 text-sm text-center text-gray-500">Sign in to continue</p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start">
              <AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={submit}>
            <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4"
              value={username}
              autoComplete="username"
              autoFocus
              onChange={(e) => setUsername(e.target.value)}
            />

            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-6"
              value={password}
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
            />

            <button
              type="submit"
              disabled={busy || !username || !password}
              className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <LogIn className="w-4 h-4 mr-2" />
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </div>
        <p className="mt-4 text-xs text-center text-gray-400">
          Sessions last 12 hours. Repeated failed attempts are temporarily locked out.
        </p>
      </div>
    </div>
  );
};

export default Login;
